import { FC, useState, MouseEvent, useEffect } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { ReferencePyramidTemplateDto, PyramidType, ReferencePyramidDetail, Country, EmployeeLevelEnum } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import { EditablePyramidsTable } from '../../pyramids/shared/EditablePyramidsTable'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { Select } from 'antd'
import { getInitialReferencePyramidData } from './defaultPyramidsUtil'
import { isR2Pyramid, isR3Pyramid } from '../../pyramids/pyramidUtils'
import { showNotification } from '../../../components/custom_components/Notification/notification'

const { Option } = Select

interface Props {
  open: boolean
  country: Country
  onCancel: (setFormikTableData: (formikTableData: ReferencePyramidDetail[]) => void) => void
  saveChanges: (values: ReferencePyramidDetail[]) => void
  handleDelete: () => void
  type: PyramidType
  initTableData?: ReferencePyramidDetail[]
  predefinedScenario?: ReferencePyramidTemplateDto[]
  createNewReference: boolean
  employeeLevels: EmployeeLevelEnum[]
}

export const CompanySettingsPyramidsEditModal: FC<Props> = ({
  open,
  country,
  onCancel,
  saveChanges,
  handleDelete,
  type,
  initTableData,
  predefinedScenario,
  createNewReference,
  employeeLevels,
}) => {
  const { t } = useTranslation()
  const [tableData, setTableData] = useState(getInitialReferencePyramidData(initTableData, type, employeeLevels))
  const [formikTableData, setFormikTableData] = useState(getInitialReferencePyramidData(initTableData, type, employeeLevels))
  const [isDeleting, toggleDeleting] = useState<boolean>(false)

  useEffect(() => {
    const newInitialData = getInitialReferencePyramidData(tableData, type, employeeLevels)
    setFormikTableData(newInitialData)
  }, [tableData, type, employeeLevels])

  useEffect(() => {
    setTableData(getInitialReferencePyramidData(initTableData, type, employeeLevels))
  }, [initTableData, type, employeeLevels])

  const getSelectedReferencePyramidTitle = (formikValues: ReferencePyramidDetail[]): string | undefined => {
    let optionFound: string | undefined = undefined
    if (formikValues && predefinedScenario) {
      for (const predefinedScenarioElement of predefinedScenario) {
        const compareFormikObject = [...formikValues]
          .map((val) => {
            return {
              range: val.range,
              limitType: val.limitType,
              percentage: val.percentage,
              displayOrder: val.displayOrder,
            }
          })
          .sort((a, b) => a.displayOrder - b.displayOrder)
        const compareTemplateObject = (
          predefinedScenarioElement.referencePyramidTemplateDetails ? [...predefinedScenarioElement.referencePyramidTemplateDetails] : []
        )
          .map((val) => {
            return {
              range: val.range,
              limitType: val.limitType,
              percentage: val.percentage,
              displayOrder: val.displayOrder,
            }
          })
          .sort((a, b) => a.displayOrder - b.displayOrder)

        if (JSON.stringify(compareFormikObject) === JSON.stringify(compareTemplateObject)) {
          optionFound = predefinedScenarioElement.title ? predefinedScenarioElement.title : undefined
        }
      }
    }
    return optionFound
  }

  const handleChange = (value: number) => {
    if (predefinedScenario) {
      const selectedPredefinedScenario: ReferencePyramidDetail[] = predefinedScenario
        .filter((scenario) => scenario.id === value)
        .flatMap((scenario) => scenario.referencePyramidTemplateDetails)
        .map((val) => {
          return {
            id: val!.id,
            range: val!.range,
            limitType: val!.limitType,
            percentage: val!.percentage,
            displayOrder: val!.displayOrder,
          }
        })

      if (selectedPredefinedScenario.length === 0) {
        // case: reset of select
        setTableData(getInitialReferencePyramidData(initTableData, type, employeeLevels))
      } else {
        setTableData(selectedPredefinedScenario)
      }
    }
  }

  const handleDeletePyramidClicked = (e: MouseEvent): void => {
    e.preventDefault()
    toggleDeleting(true)
    handleDelete()
    const pyramidType = type === PyramidType.Level ? 'Level' : 'Age'
    showNotification({
      type: 'success',
      message: t('companySettingsView.pyramidsTab.editModal.notifications.delete.success.title'),
      description: t('companySettingsView.pyramidsTab.editModal.notifications.delete.success.description', { pyramidType }),
    })
    toggleDeleting(false)
  }

  return (
    <Formik
      initialValues={formikTableData}
      enableReinitialize={true}
      onSubmit={(values: ReferencePyramidDetail[], { setSubmitting, resetForm }) => {
        setSubmitting(true)
        const newValues = values.map((v) => ({ ...v }))

        //values need to be fixed because c1 and c2 pyramids use the c1 limit in some cases!!!
        if (type === PyramidType.Level) {
          if (isR3Pyramid(country) || isR2Pyramid(country)) {
            const c2Index = newValues.findIndex((reference) => reference.range === 'C2')
            const c1Index = newValues.findIndex((reference) => reference.range === 'C1')
            const prIndex = newValues.findIndex((reference) => reference.range === 'PR')
            const mIndex = newValues.findIndex((reference) => reference.range === 'M')
            const sprIndex = newValues.findIndex((reference) => reference.range === 'SPR')
            const smIndex = newValues.findIndex((reference) => reference.range === 'SM')

            if (c2Index !== -1 && c1Index !== -1) {
              newValues[c2Index].limitType = newValues[c1Index].limitType
            }
            if (prIndex !== -1 && mIndex !== -1) {
              newValues[mIndex].limitType = newValues[prIndex].limitType
            }
            if (sprIndex !== -1 && smIndex !== -1) {
              newValues[smIndex].limitType = newValues[sprIndex].limitType
            }
          } else {
            const c2Index = newValues.findIndex((reference) => reference.range === 'C2')
            const c1Index = newValues.findIndex((reference) => reference.range === 'C1')

            if (c2Index !== -1 && c1Index !== -1) {
              newValues[c2Index].limitType = newValues[c1Index].limitType
            }
          }
        }

        saveChanges(newValues)
        setFormikTableData(newValues)
        resetForm()
        const operation = createNewReference ? 'create' : 'update'
        const pyramidType = type === PyramidType.Level ? 'Level' : 'Age'
        showNotification({
          type: 'success',
          message: t(`companySettingsView.pyramidsTab.editModal.notifications.${operation}.success.title`),
          description: t(`companySettingsView.pyramidsTab.editModal.notifications.${operation}.success.description`, { pyramidType }),
        })
        setSubmitting(false)
      }}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {(props) => {
        const { isSubmitting, handleSubmit, handleReset, values } = props
        return (
          <PerModal
            title={{
              heading: createNewReference
                ? t('companySettingsView.pyramidsTab.editModal.headingCreateNew', { type: type.toLowerCase() })
                : t('companySettingsView.pyramidsTab.editModal.heading', { type: type.toLowerCase() }),
            }}
            size="small"
            visible={open}
            onCancel={() => {
              handleReset()
              onCancel(setFormikTableData)
            }}
            footer={{
              formikForm: {
                onSubmit: handleSubmit,
                onReset: handleReset,
              },
              cancelBtn: {
                onClick: () => {
                  onCancel(setFormikTableData)
                },
              },
              confirmBtn: {
                text: 'save',
                onClick: handleSubmit,
                disabled: isSubmitting,
                isProcessing: { active: isSubmitting },
              },
            }}
          >
            <PerFlexRow justify="space-between" align="middle" mb="m" noWrap>
              <Select
                placeholder={t('companySettingsView.pyramidsTab.editModal.predefinedScenario.placeholder')}
                onChange={handleChange}
                className="fullWidth"
                value={getSelectedReferencePyramidTitle(values) as any /* strange type mismatch number <=> string */}
                notFoundContent={t('companySettingsView.pyramidsTab.editModal.predefinedScenario.empty')}
              >
                {predefinedScenario &&
                  predefinedScenario.map((scenario: ReferencePyramidTemplateDto) => (
                    <Option key={scenario.id} value={scenario.id}>
                      {scenario.title}
                    </Option>
                  ))}
              </Select>
              {!createNewReference && (
                <PerButtonCircle
                  icon="delete_fill"
                  color="error"
                  variant="pastel"
                  tooltip={'Delete ' + type + ' Reference Pyramid for this company.'}
                  ml="m"
                  type="button"
                  onClick={(event: MouseEvent) => {
                    handleDeletePyramidClicked(event)
                  }}
                  disabled={isDeleting}
                  isProcessing={{ active: isDeleting }}
                />
              )}
            </PerFlexRow>
            <EditablePyramidsTable type={type} formikValues={values} country={country} />
          </PerModal>
        )
      }}
    </Formik>
  )
}
